import axios from 'axios';
import { PlaceAutocomplete } from './PlaceAutocomplete';
import { PlaceDetail, Result } from './PlaceDetail';
import { Candidate } from './../../components/search/Candidate';
import Place from './Place';
import { Lang } from '../../states/query';
import { formatAddress } from '../public/googleApi';

interface ApiConfig {
  baseURL: string;
  timeout: number;
}

const DEFAULT_API_CONFIG: ApiConfig = {
  baseURL: process.env['REACT_APP_API_ENDPOINT'] as string,
  timeout: 7000,
};

/**
 * Google Places APIから候補を取得する処理。
 * @param input テキストボックスで入力した値。
 * @param lang 言語指定（ja/en)
 * @param optionConfig
 */
export async function getClosedAutocomplete (input: string, lang: string = 'ja', optionConfig?: ApiConfig) {
  const config = {
    ...DEFAULT_API_CONFIG,
    ...optionConfig,
  };
  const instance = axios.create(config);
  const params = {
    // 入力文字列に補正をかける
    input,
    // 地点候補の言語指定。
    language: lang,
    // 日本のデータのみ取得するよう指定。
    components: 'country:jp',
  };
  // あとで見直す（2020/02/20現在）
  // eslint-disable-next-line no-useless-catch
  try {
    const response = await instance.get(`google/places/autocomplete`, { params });

    // ステータスコードが200ではない場合。
    if (response.status !== 200) {
      throw new Error('Server Error');
    }

    // 施設（地点）のデータを取得する。
    const placeAutocomplete: PlaceAutocomplete = response.data;
    // 取得した施設（地点）のデータを画面上に表示する候補の形式に変換する。
    const candidates = placeAutocomplete.predictions.map((p) => {
      const name = p.structured_formatting ? p.structured_formatting.main_text : p.description;
      return new Candidate(name, p.place_id, 'place', undefined, undefined, formatAddress(p.description))
    })
    return candidates;
  } catch (err) {
    throw err;
  }
}

/**
 * 地点詳細（/places/detail）を取得する。
 * （AutoCompleteで取得した地点の緯度経度を取得するために利用する。）
 * @param place_id ユーザーが指定した地点のID。（AutoCompleteで取得したplace_id）
 * @param optionConfig
 */
export async function getClosedPlaceDetail(place_id: string, optionConfig?: ApiConfig, lang: Lang = Lang.ja) {
  const config = {
    ...DEFAULT_API_CONFIG,
    ...optionConfig,
  };
  const instance = axios.create(config);
  const params = {
    place_id,
    fields: 'geometry,address_components,type,name,formatted_address',
    // テキスト部分はログ記録時のみ利用するため、日本語で固定とする。（2020/07/15現在。）
    // タクシー仮予約画面多言語た対応のため、該当箇所は指定の言語で取得できるようにする（2022/02/22現在）
    language: lang
  };
  // あとで見直す（2020/02/20現在）
  // eslint-disable-next-line no-useless-catch
  try {
    const response = await instance.get(`google/places/detail`, { params });

    // ステータスコードが200ではない場合。
    if (response.status !== 200) {
      throw new Error('Server Error');
    }
    // Place Detailsのレスポンス部分。
    const data: PlaceDetail = response.data;
    if(!response.data.result) {
      throw new Error(response.data.error_message)
    }
    // Response.status結果が異常の場合にはエラーを返す
    if(data.status !== 'OK') {
      throw new Error(JSON.stringify(data));
    }
    // 施設（地点）の詳細データをインスタンス化して返す。
    return new Place(data.result);
  } catch (err) {
    throw err;
  }
}

/**
 * 位置情報より地点詳細情報を取得する（/places/geocode/）
 */
export async function getClosedGeocode(latlng: string, lang: Lang = Lang.ja, optionConfig?: ApiConfig) {
  const config = {
    ...DEFAULT_API_CONFIG,
    ...optionConfig,
  };
  const instance = axios.create(config);
  const params = {
    latlng,
    language: lang
  };
  // あとで見直す（2020/02/20現在）
  // eslint-disable-next-line no-useless-catch
  try {
    const response = await instance.get(`google/places/geocode`, { params });

    // ステータスコードが200ではない場合。
    if (response.status !== 200) {
      throw new Error('Server Error');
    }

    // 施設（地点）の詳細データを取得する。
    return response.data.results.map((x: Result)=>{
      return new Place(x);
    });
  } catch (err) {
    throw err;
  }
}
